import { Link } from "react-router-dom";
import { IconArrowRise } from "@/components/landing/icons";

/**
 * How every public page ends.
 *
 * Five pages each had their own copy of a dark slab with a white button —
 * same idea, five slightly different paddings, and five places to edit. This
 * is the one closing, and it is in MORNING light rather than on black.
 *
 * That is the deliberate part. The site's whole visual argument is a sunrise:
 * the dark is the hour before, and the pages climb out of it. Ending on a
 * black slab — which is what every other SaaS page does — would spend the
 * concept and then contradict it at the last screen the visitor sees.
 *
 * Same light direction as every dark Stage: low, and from the left.
 */
export default function ClosingCTA({
  title,
  lede,
  to = "/signup",
  label = "Start free — no card",
  testId,
  footer,
}) {
  return (
    <section className="relative overflow-hidden bg-paper" data-testid="closing-cta">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 motion-safe:animate-daybreak"
        style={{
          background:
            // A full-width wash underneath the two warm pools, so the light
            // reaches the right edge instead of stopping in a visible band.
            "linear-gradient(to top, rgba(255,183,133,0.22), transparent 58%)," +
            "radial-gradient(70rem 32rem at 8% 112%, rgba(255,143,94,0.36), transparent 64%)," +
            "radial-gradient(60rem 28rem at 52% 122%, rgba(255,201,120,0.32), transparent 62%)," +
            "radial-gradient(60rem 30rem at 96% -20%, rgba(0,47,167,0.07), transparent 66%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 py-12 lg:py-16 lg:py-20 text-center">
        <h2 className="font-heading font-bold tracking-tighter text-stone-950 text-[length:var(--type-h2)] leading-[1.05]">
          {title}
        </h2>
        {lede && <p className="mt-4 text-stone-600 max-w-xl mx-auto">{lede}</p>}
        <Link
          to={to}
          data-testid={testId}
          className="mt-9 h-12 px-8 inline-flex items-center justify-center gap-2 bg-night text-white hover:bg-night-lift rounded-sm font-medium transition-colors shadow-[var(--shadow-lift)]"
        >
          {label}
          <IconArrowRise className="w-4 h-4" />
        </Link>
        {/* After the button, never before it — anything between the promise
            and the control is a speed bump on the last step of the funnel. */}
        {footer}
      </div>
    </section>
  );
}
